import { useState } from 'react';
import { Cpu, Copy, CheckCircle2, X, Target, FileText, Globe } from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { StrategicAgent } from '@/hooks/useStrategicStream';

export function StrategicAgentModal({ agent, workstreamName, onClose }: {
  agent: StrategicAgent;
  workstreamName: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (agent.fullOutput) {
      await navigator.clipboard.writeText(agent.fullOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-border/50 bg-surface shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-surface/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {agent.agentRole || `Agent ${agent.agentIndex + 1}`}
              </h3>
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <span>{workstreamName}</span>
                <span className="text-text-muted">|</span>
                <span className="font-mono">{agent.model}</span>
                {agent.latencySeconds && (
                  <>
                    <span className="text-text-muted">|</span>
                    <span>{agent.latencySeconds.toFixed(1)}s</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="px-3 py-1.5 rounded-lg bg-surface-light hover:bg-surface-raised text-text-primary text-sm flex items-center gap-1.5 transition-colors"
            >
              {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-surface-light text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-4">
          {/* Key Findings */}
          {agent.keyFindings && agent.keyFindings.length > 0 && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
              <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Key Findings
              </h4>
              <ul className="space-y-1.5 text-sm text-text-primary">
                {agent.keyFindings.map((finding, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 mt-2 flex-shrink-0" />
                    <MarkdownContent content={finding} className="flex-1 text-sm [&_p]:inline" inline />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Full Output */}
          <div className="rounded-xl bg-surface-light/50 border border-border/50 p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Full Response
            </h4>
            <div className="text-sm text-text-primary leading-relaxed">
              <MarkdownContent content={agent.fullOutput || agent.outputPreview || 'No output available'} className="text-sm" />
            </div>
          </div>

          {/* Sources */}
          {agent.sources && agent.sources.length > 0 && (
            <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 p-4">
              <h4 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Sources ({agent.sources.length})
              </h4>
              <div className="space-y-2">
                {agent.sources.map((source, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-text-muted flex-shrink-0">{i + 1}.</span>
                    {source.url ? (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1"
                      >
                        {source.title}
                      </a>
                    ) : (
                      <span className="text-text-secondary">{source.title}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
